// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Appearance > Sidebar > Pages (desktop). Shows, hides and reorders the
// sidebar's page buttons; Home is always shown and always first, and spaces
// are not in the list (they keep their place under Chats). Drag the handle,
// or focus it and use the arrow keys. A miniature of the sidebar on the right
// shows the result and names the hidden pages, which the avatar pop-up lists
// under "More". Saved with the account.

import {computeVerticalDropPosition} from '@app/features/app/components/layout/dnd/DndDropPosition';
import {SIDEBAR_PAGE_LABELS, SidebarPageIcon} from '@app/features/app/components/layout/sidebar_nav/SidebarPageLabels';
import {
	availableSidebarPages,
	type DesktopSidebarPrefs,
	hiddenSidebarPages,
	isSidebarPageVisible,
	moveSidebarPage,
	type SidebarPageId,
	setSidebarPageHidden,
	sidebarPageLayout,
} from '@app/features/app/components/layout/sidebar_nav/SidebarPages';
import {
	currentDesktopSidebar,
	currentSidebarPageConditions,
} from '@app/features/app/components/layout/sidebar_nav/SidebarPagesLive';
import KonvoCalendarPrefs from '@app/features/konvo_calendar/state/KonvoCalendarPrefs';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {StatusAwareAvatar} from '@app/features/ui/components/StatusAwareAvatar';
import {DotsSixVerticalIcon} from '@app/features/ui/icons/Icons';
import styles from '@app/features/user/components/modals/tabs/appearance_tab/SidebarPagesEditor.module.css';
import Users from '@app/features/user/state/Users';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import type {ConnectableElement} from 'react-dnd';
import {DndProvider, useDrag, useDrop} from 'react-dnd';
import {getEmptyImage, HTML5Backend} from 'react-dnd-html5-backend';

const PAGES_DESCRIPTOR = msg({
	message: 'Pages',
	comment: 'Heading in Settings > Appearance > Sidebar above the list of sidebar page buttons.',
});
const PAGES_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Choose what the sidebar shows and drag to reorder.',
	comment: 'Description under the Pages heading in Settings > Appearance > Sidebar.',
});
const PREVIEW_DESCRIPTOR = msg({
	message: 'Preview',
	comment: 'Heading above the miniature of the sidebar in Settings > Appearance > Sidebar.',
});
const ALWAYS_SHOWN_DESCRIPTOR = msg({
	message: 'Always shown',
	comment: 'Note under Home in Settings > Appearance > Sidebar > Pages: Home cannot be hidden.',
});
const AGENTS_NOTE_DESCRIPTOR = msg({
	message: "Your coding agents' chats",
	comment: 'Note under Agents in Settings > Appearance > Sidebar > Pages: the page lists coding agent threads.',
});
const SCHEDULED_NOTE_DESCRIPTOR = msg({
	message: 'Reminders, scheduled sends and automations. Also opens from Home',
	comment:
		'Note under Scheduled in Settings > Appearance > Sidebar > Pages: what the page lists, and that the Home header button still opens it when the sidebar button is off.',
});
const NEW_DESCRIPTOR = msg({
	message: 'New',
	comment: 'Small pill next to a page that was just added to Konvo, in Settings > Appearance > Sidebar > Pages.',
});
const SHOW_PAGE_DESCRIPTOR = msg({
	message: 'Show {label} in the sidebar',
	comment:
		'Accessible label for the switch on a page in Settings > Appearance > Sidebar > Pages. {label} is a page name.',
});
const REORDER_PAGE_DESCRIPTOR = msg({
	message: 'Reorder {label}. Drag, or use the up and down arrow keys.',
	comment:
		'Accessible label for the drag handle on a page in Settings > Appearance > Sidebar > Pages. {label} is a page name.',
});
const HIDDEN_DESCRIPTOR = msg({
	message: 'Hidden',
	comment: 'Heading beside the sidebar miniature listing the pages switched off.',
});
const HIDDEN_HINT_DESCRIPTOR = msg({
	message: 'Open them from your picture at the bottom.',
	comment: 'Hint beside the sidebar miniature: hidden pages are in the pop-up that opens from the user avatar.',
});
const NOTHING_HIDDEN_DESCRIPTOR = msg({
	message: 'Nothing hidden',
	comment: 'Beside the sidebar miniature when every page is shown.',
});
const NOTHING_HIDDEN_HINT_DESCRIPTOR = msg({
	message: 'Pages you switch off move to the pop-up on your picture at the bottom.',
	comment: 'Hint beside the sidebar miniature when every page is shown.',
});

const PAGE_DRAG_TYPE = 'konvo-sidebar-page';

interface PageDragItem {
	id: SidebarPageId;
	index: number;
}

interface PageRowProps {
	id: SidebarPageId;
	index: number;
	visible: boolean;
	onToggle: (id: SidebarPageId, show: boolean) => void;
	onMove: (id: SidebarPageId, toIndex: number) => void;
	onDragEnd: () => void;
}

const PageRow = observer(({id, index, visible, onToggle, onMove, onDragEnd}: PageRowProps) => {
	const {i18n} = useLingui();
	const rowRef = useRef<HTMLDivElement | null>(null);
	const handleRef = useRef<HTMLButtonElement | null>(null);
	const locked = id === 'home';
	const label = i18n._(SIDEBAR_PAGE_LABELS[id]);
	const [{isDragging}, dragRef, preview] = useDrag(
		() => ({
			type: PAGE_DRAG_TYPE,
			item: (): PageDragItem => ({id, index}),
			canDrag: () => !locked,
			collect: (monitor) => ({isDragging: monitor.isDragging()}),
			end: () => onDragEnd(),
		}),
		[id, index, locked, onDragEnd],
	);
	const [, dropRef] = useDrop(
		() => ({
			accept: PAGE_DRAG_TYPE,
			hover: (item: PageDragItem, monitor) => {
				// Home stays first; nothing moves above it.
				if (item.id === id || locked) return;
				const node = rowRef.current;
				const offset = monitor.getClientOffset();
				if (!node || !offset) return;
				const position = computeVerticalDropPosition(offset, node.getBoundingClientRect());
				// Only swap once the pointer crosses the middle of the row, so rows do not flicker.
				if (item.index < index && position === 'before') return;
				if (item.index > index && position === 'after') return;
				onMove(item.id, index);
				item.index = index;
			},
		}),
		[id, index, locked, onMove],
	);
	useEffect(() => {
		preview(getEmptyImage(), {captureDraggingState: true});
	}, [preview]);
	const setRowRef = useCallback(
		(node: HTMLDivElement | null) => {
			rowRef.current = node;
			dropRef(node as ConnectableElement);
		},
		[dropRef],
	);
	const setHandleRef = useCallback(
		(node: HTMLButtonElement | null) => {
			handleRef.current = node;
			dragRef(node as ConnectableElement);
		},
		[dragRef],
	);
	const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
		if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
			event.preventDefault();
			onMove(id, index + (event.key === 'ArrowUp' ? -1 : 1));
			onDragEnd();
			// Moving the row can move its node in the DOM, which drops focus; keep it on the handle.
			requestAnimationFrame(() => handleRef.current?.focus());
		}
	};
	const note =
		id === 'home'
			? ALWAYS_SHOWN_DESCRIPTOR
			: id === 'agents'
				? AGENTS_NOTE_DESCRIPTOR
				: id === 'scheduled'
					? SCHEDULED_NOTE_DESCRIPTOR
					: null;
	return (
		<div
			ref={setRowRef}
			role="listitem"
			className={clsx(styles.row, isDragging && styles.rowDragging, !visible && styles.rowOff)}
			data-page={id}
			data-flx="user.appearance-tab.sidebar-pages.row"
		>
			{locked ? (
				<span className={clsx(styles.handle, styles.handleLocked)} aria-hidden="true">
					<DotsSixVerticalIcon size={18} />
				</span>
			) : (
				<button
					ref={setHandleRef}
					type="button"
					className={styles.handle}
					aria-label={i18n._(REORDER_PAGE_DESCRIPTOR, {label})}
					onKeyDown={handleKeyDown}
					data-flx="user.appearance-tab.sidebar-pages.handle"
				>
					<DotsSixVerticalIcon size={18} />
				</button>
			)}
			<span className={styles.tile} aria-hidden="true">
				<SidebarPageIcon page={id} size={18} />
			</span>
			<span className={styles.text}>
				<span className={styles.name}>
					{label}
					{(id === 'agents' || id === 'scheduled') && <span className={styles.newPill}>{i18n._(NEW_DESCRIPTOR)}</span>}
				</span>
				{note && <span className={styles.note}>{i18n._(note)}</span>}
			</span>
			<Switch
				compact
				value={visible}
				disabled={locked}
				onChange={(value) => onToggle(id, value)}
				ariaLabel={i18n._(SHOW_PAGE_DESCRIPTOR, {label})}
				className={styles.switch}
				data-flx="user.appearance-tab.sidebar-pages.switch"
			/>
		</div>
	);
});

/** The sidebar in miniature: Home, the pages around the Chats block, a space, and the avatar. */
const SidebarMiniature = observer(({prefs}: {prefs: DesktopSidebarPrefs}) => {
	const conditions = currentSidebarPageConditions();
	const layout = sidebarPageLayout(conditions, prefs);
	const chatsShown = isSidebarPageVisible('chats', conditions, prefs);
	const user = Users.currentUser;
	return (
		<div className={styles.miniRail} aria-hidden="true" data-flx="user.appearance-tab.sidebar-pages.mini-rail">
			<span className={clsx(styles.miniIcon, styles.miniIconActive)}>
				<SidebarPageIcon page="home" size={16} />
			</span>
			<span className={styles.miniDivider} />
			{layout.beforeChats.map((id) => (
				<span key={id} className={styles.miniIcon}>
					<SidebarPageIcon page={id} size={16} />
				</span>
			))}
			{chatsShown && (
				<span className={styles.miniIcon}>
					<SidebarPageIcon page="chats" size={16} />
				</span>
			)}
			<span className={styles.miniSpace} />
			{layout.afterChats.map((id) => (
				<span key={id} className={styles.miniIcon}>
					<SidebarPageIcon page={id} size={16} />
				</span>
			))}
			<span className={styles.miniSpacer} />
			{user && <StatusAwareAvatar user={user} size={24} disableStatusTooltip={true} className={styles.miniAvatar} />}
		</div>
	);
});

export const SidebarPagesEditor: React.FC = observer(() => {
	const {i18n} = useLingui();
	useEffect(() => {
		void KonvoCalendarPrefs.load();
	}, []);
	const saved = currentDesktopSidebar();
	// While a drag is under way the list follows the pointer locally; it is saved on drop.
	const [draft, setDraft] = useState<DesktopSidebarPrefs | null>(null);
	const draftRef = useRef<DesktopSidebarPrefs | null>(null);
	draftRef.current = draft;
	const prefs = draft ?? saved;
	const conditions = currentSidebarPageConditions();
	const shown = availableSidebarPages(conditions, prefs);
	const hidden = hiddenSidebarPages(conditions, prefs);

	const handleToggle = useCallback((id: SidebarPageId, show: boolean) => {
		void KonvoCalendarPrefs.save({desktopSidebar: setSidebarPageHidden(currentDesktopSidebar(), id, !show)});
	}, []);
	const handleMove = useCallback((id: SidebarPageId, toIndex: number) => {
		const base = draftRef.current ?? currentDesktopSidebar();
		const next = moveSidebarPage(base, availableSidebarPages(currentSidebarPageConditions(), base), id, toIndex);
		draftRef.current = next;
		setDraft(next);
	}, []);
	const handleDragEnd = useCallback(() => {
		const next = draftRef.current;
		draftRef.current = null;
		setDraft(null);
		if (next) void KonvoCalendarPrefs.save({desktopSidebar: next});
	}, []);

	return (
		<div className={styles.pages} data-flx="user.appearance-tab.sidebar-pages">
			<div className={styles.listColumn}>
				<div className={styles.heading} id="sidebar-pages-heading">
					{i18n._(PAGES_DESCRIPTOR)}
				</div>
				<div className={styles.description}>{i18n._(PAGES_DESCRIPTION_DESCRIPTOR)}</div>
				<DndProvider backend={HTML5Backend}>
					<div
						className={styles.list}
						role="list"
						aria-labelledby="sidebar-pages-heading"
						data-flx="user.appearance-tab.sidebar-pages.list"
					>
						{shown.map((id, index) => (
							<PageRow
								key={id}
								id={id}
								index={index}
								visible={isSidebarPageVisible(id, conditions, prefs)}
								onToggle={handleToggle}
								onMove={handleMove}
								onDragEnd={handleDragEnd}
							/>
						))}
					</div>
				</DndProvider>
			</div>
			<div className={styles.previewColumn}>
				<div className={styles.heading}>{i18n._(PREVIEW_DESCRIPTOR)}</div>
				<div className={styles.previewCard} data-flx="user.appearance-tab.sidebar-pages.preview">
					<SidebarMiniature prefs={prefs} />
					<div className={styles.previewText}>
						<span className={styles.previewTitle}>
							{i18n._(hidden.length > 0 ? HIDDEN_DESCRIPTOR : NOTHING_HIDDEN_DESCRIPTOR)}
						</span>
						{hidden.length > 0 && (
							<span className={styles.previewList}>
								{hidden.map((id) => i18n._(SIDEBAR_PAGE_LABELS[id])).join(', ')}
							</span>
						)}
						<span className={styles.previewHint}>
							{i18n._(hidden.length > 0 ? HIDDEN_HINT_DESCRIPTOR : NOTHING_HIDDEN_HINT_DESCRIPTOR)}
						</span>
					</div>
				</div>
			</div>
		</div>
	);
});
